import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import VerdictBadge from "@/components/VerdictBadge";
import { deskFor } from "@/lib/deskAccents";

const TYPES = [
  { value: "", label: "All content" },
  { value: "article", label: "Articles" },
  { value: "analysis", label: "Analysis" },
  { value: "fact_check", label: "Fact-checks" },
];

const routeFor = (item) => `/${item.type.replace("_", "-")}/${item.slug}`;

function StoryCard({ item, testid, accent }) {
  return (
    <Link to={routeFor(item)} data-testid={testid} className={`group block border-t-2 ${accent.topBorder} pt-4`}>
      <div className="flex items-center gap-2">
        <span className="label-caps text-ink/55">{item.label}</span>
        {item.verdict && <VerdictBadge verdict={item.verdict} />}
      </div>
      <h4 className="mt-3 font-editorial font-bold text-xl leading-snug text-ink group-hover:underline decoration-2 underline-offset-4">{item.title}</h4>
      <p className="mt-2 text-sm text-ink/70 line-clamp-3">{item.summary}</p>
    </Link>
  );
}

export default function Section({ category, description }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const accent = deskFor(category);

  useEffect(() => {
    setLoading(true);
    api.get("/public/content", { params: { category, type: type || undefined } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, type]);

  // Section-specific keyword search filters ONLY over already-published section content.
  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.toLowerCase();
    return items.filter((i) => (i.title + " " + (i.summary || "")).toLowerCase().includes(t));
  }, [items, q]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid={`section-${category.toLowerCase()}`}>
      <title>{`${category} | Be-Apuo Media Intelligence`}</title>
      <div className={`label-caps ${accent.eyebrow}`}>Section</div>
      <h1 className="mt-2 font-editorial font-black text-4xl sm:text-5xl tracking-tight text-ink">{category}</h1>
      {description && <p className="mt-3 max-w-2xl text-ink/70 leading-relaxed">{description}</p>}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          data-testid="section-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${category}`}
          className="w-full sm:max-w-xs border border-ink/20 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-heritage"
        />
        <select data-testid="section-type-filter" value={type} onChange={(e) => setType(e.target.value)}
          className="border border-ink/20 bg-white px-2 py-2 text-sm text-ink focus:outline-none focus:border-heritage">
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div className={`mt-10 border-t-2 ${accent.topBorder} pt-8`}>
        {loading ? (
          <p className="text-ink/55" data-testid="section-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="border border-ink/10 bg-mist p-12 text-center" data-testid="section-empty">
            <p className="text-ink/60">No published content is available in this section yet.</p>
          </div>
        ) : (
          <>
            {featured && (
              <Link to={routeFor(featured)} data-testid={`section-featured-${featured.slug}`} className="group block mb-12">
                <div className={`label-caps ${accent.eyebrow}`}>Featured</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="label-caps text-ink/55">{featured.label}</span>
                  {featured.verdict && <VerdictBadge verdict={featured.verdict} />}
                </div>
                <h2 className="mt-2 font-editorial font-black text-3xl sm:text-4xl leading-tight text-ink group-hover:underline decoration-2 underline-offset-4">{featured.title}</h2>
                <p className="mt-3 max-w-3xl text-ink/70 line-clamp-3">{featured.summary}</p>
              </Link>
            )}
            {rest.length > 0 && (
              <>
                <div className="label-caps text-ink/55 mb-4 border-t border-ink/10 pt-6">Latest</div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
                  {rest.map((item) => <StoryCard key={item.id} item={item} testid={`section-card-${item.slug}`} accent={accent} />)}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
